import { ReferenceNodeType } from "~/enums/referenceNodeType";
import { ObjectHelper } from "~/helpers/objectHelper";
import { Hash } from "~/interfaces/hashInterfaces";
import { DatetimeMode } from "~/models/datetimeMode";
import MappedPropertyLinkedDetailView from "~/models/detailView/elements/mappedPropertyLinkedDetailview";
import {
  ObjectModelBuiltinPropertyType,
  ObjectModelBuiltinPropertyTypeName
} from "../../../common/enums/objectModelBuiltinPropertyType";
import { ObjectModelPropertyName, ObjectModelPropertyRootType } from "../../../common/enums/objectModelPropertyType";
import DetailViewContentElement from "../../../common/models/detailView/elements/detailViewContentElement";
import InputFormatDefinition from "../../../common/models/inputFormatDefinition";
import ObjectModelProperty from "../../../common/models/objectModelProperties/objectModelProperty";
import ObjectModelPropertyDefinition from "../../../common/models/objectModelProperties/objectModelPropertyDefinition";
import { InputFormatType } from "../../form_builder/models/inputFormatType";

export default class ModelProperty extends DetailViewContentElement {
  property_id: string;
  placeholder_text: string;
  builtin_property_type: ObjectModelBuiltinPropertyType | null;
  mapped_property_linked_detail_views: Hash<MappedPropertyLinkedDetailView> = {};
  date_display_format: number;

  get mappedPropertyLinkedDetailViewsArray(): MappedPropertyLinkedDetailView[] {
    return ObjectHelper.toArray(this.mapped_property_linked_detail_views)
      .sort();
  }


  constructor() {
    super();
    this.defineProperty('property_id', null);
    this.defineProperty('placeholder_text', null);
    this.defineProperty('builtin_property_type', null);
    this.defineProperty('date_display_format', null);

  }

  get property(): ObjectModelProperty {
    // if pre-loaded on page load
    if (this?.reference_path?.last_referenced_object) {
      const lastReferencedObject = this.reference_path.last_referenced_object
      const prop = ObjectModelProperty.repo.all().find(p => p.id === lastReferencedObject.id)
      if (prop) {
        return prop;
      }
      return ObjectModelProperty.repo.findOrCreate(lastReferencedObject.id).loadFromJson(lastReferencedObject)
    }

    // if selected
    if (this.reference_path?.nodes?.length > 0) {
      const property = this.reference_path.nodes[this.reference_path.nodes.length - 1];
      return ObjectModelProperty.repo.all().find(p => p.id === property.reference_id)
    }

    return this.objectModel.findPropertyById(this.property_id)
  }

  get builtinPropertyType() {
    const builtIn = this.reference_path?.nodes[this.reference_path.nodes.length - 1]
    if (builtIn && builtIn.reference_type === ReferenceNodeType.ObjectModelRecordMetadata){
      return parseInt(builtIn.reference_id, 10);
    }

    return this.builtin_property_type;
  }

  get propertyNotConfigured(): boolean {
    return !this.property && !this.builtinPropertyType && !this.reference_path?.lastNode?.service?.label;
  }

  get propertyIcon(): string {

    if (this.isNestedReference) {
      return "related-object"
    }

    if (this.property) {
      return ObjectModelPropertyDefinition.getIcon(this.property.property_type)
    } 
    
    if (this.builtinPropertyType) {
      switch (this.builtinPropertyType) {
        case ObjectModelBuiltinPropertyType.CreatedBy:
          return ObjectModelPropertyDefinition.getIcon(ObjectModelPropertyRootType.Text);
        case ObjectModelBuiltinPropertyType.CreatedAt:
        case ObjectModelBuiltinPropertyType.UpdatedAt:
          return ObjectModelPropertyDefinition.getIcon(ObjectModelPropertyRootType.DateTime);
      }
    }
  }

  get propertyName(): string {
    if (this.reference_path?.lastNode?.service.label) {
      return this.reference_path.nicePathString
    } else if (!!this.property) {
      return this.property.hasLabelTitle ? this.property.label_title : "None"
    } else if (this.builtinPropertyType) {
      return ObjectModelBuiltinPropertyTypeName[this.builtinPropertyType];
    } 
  }

  get propertyType(): string {
    if (!!this.property) {
      return ObjectModelPropertyName(this.property);
    } else if (this.builtinPropertyType) {
      switch (this.builtinPropertyType) {
        case ObjectModelBuiltinPropertyType.CreatedBy:
          return ObjectModelPropertyName(ObjectModelPropertyRootType.Text);
        case ObjectModelBuiltinPropertyType.CreatedAt:
        case ObjectModelBuiltinPropertyType.UpdatedAt:
          return ObjectModelPropertyName(ObjectModelPropertyRootType.DateTime);
      }
    }
  }

  get propertyIsUrlFormat(): boolean {
    return this.propertyIsShortText && this.property.format_type === InputFormatType.Url
  }

  get propertyIsShortText(): boolean {
    return this.property.property_type === ObjectModelPropertyRootType.Text;
  }

  get propertyIsDateTime(): boolean {
    const property = this.property;

    if (property) {
      return property.property_type === ObjectModelPropertyRootType.DateTime || (property.property_type === ObjectModelPropertyRootType.MappedProperty && property.mapped_property_type === ObjectModelPropertyRootType.DateTime)
    //Handle for built in properties
    } else {
     return this.builtinPropertyType === ObjectModelBuiltinPropertyType.CreatedAt || this.builtinPropertyType === ObjectModelBuiltinPropertyType.UpdatedAt
    }
  }

  get propertyDatetimeMode(): DatetimeMode {
    if (this.propertyIsDateTime) {
      let property = this.property;
      if (property) {
        if (property.property_type === ObjectModelPropertyRootType.MappedProperty) {
          //Check to see if we are mapping over a default property
          return property.linked_relation_mapped_properties[0].default_property ? DatetimeMode.Datetime : property.mapped_property_datetime_mode
        } else {
          return property.datetime_mode
        }
      } else {
        return DatetimeMode.Datetime
      }
    }
  }

  get propertyFormat(): string {
    if (this.property.canHaveFormat) {
      return this.property.format_type ? InputFormatDefinition.getDisplayName(this.property.format_type) : "None";
    } else {
      return "N/A";
    }
  }

  get propertyCharLimit(): string {
    if (this.property.canHaveCharacterCount) {
      if (!this.property.hasMinOrMaxLength) {
        return "None"
      } else if (this.property.character_count.min && !this.property.character_count.max) {
        return `${this.property.character_count.min} Character Minimum`
      } else if (!this.property.character_count.min && this.property.character_count.max) {
        return `${this.property.character_count.max} Character Maximum`
      } else if (this.property.character_count.min === this.property.character_count.max) {
        return `${this.property.character_count.min} Characters`
      } else {
        return `${this.property.character_count.min} - ${this.property.character_count.max} Characters`
      }
    } else {
      return "N/A";
    }
  }

  get propertyRequired(): string {
    if (!this.property.canBeRequired) {
      return "N/A"
    }
    return this.property.is_required ? "Yes" : "No"
  }

  get propertyIndexed(): string {
    if (!this.property.isIndexable) {
      return "N/A"
    } else {
      return this.property.isIndexed ? "Yes" : "No"
    }
  }

  get propertyClientId(): string {
    if (!!this.property) return this.property.client_id;
  }

  get isModelProperty(): boolean {
    return true
  }

  createMappingPropertyLinkedDetailViews(json: {property_client_id: string, detail_view_id: string}) {
    const newMappedPropertyLinkedDetailView = this.createEmbeddedObject.mapped_property_linked_detail_views(this, json)
    this.initializeEmbedded(this, newMappedPropertyLinkedDetailView, 0, {}, false)
  }

  get isReferenceObjectModelRelation(): boolean {
    if (!this.reference_path) return false;
    return this.reference_path.lastNode.reference_type === ReferenceNodeType.ObjectModelRelation
  }

  // does reference path not refer to current object model
  get isNestedReference(): boolean {
    if (!this.reference_path) return false;

    const omIndex = this.reference_path.nodes.length - 2;

    const detailViewOMId = this.rootModel.object_model_id;
    const referenceOMId = this.reference_path.nodes[omIndex].reference_id;
    return (detailViewOMId !== referenceOMId) || this.isReferenceObjectModelRelation;
  }
}
